import React from 'react';
import { cn, getInitials, getAvatarColor } from '@/lib/utils';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';

export interface AvatarProps {
  /** Source URL for the avatar image */
  src?: string | null;
  /** Display name — used for initials fallback and alt text */
  name?: string | null;
  size?: AvatarSize;
  className?: string;
  /** Optional status indicator */
  status?: 'online' | 'away' | 'offline';
}

const sizeStyles: Record<AvatarSize, string> = {
  xs: 'h-6 w-6 text-2xs',
  sm: 'h-8 w-8 text-xs',
  md: 'h-9 w-9 text-sm',
  lg: 'h-11 w-11 text-base',
  xl: 'h-14 w-14 text-lg',
  '2xl': 'h-28 w-28 text-3xl',
  '3xl': 'h-32 w-32 text-4xl',
};

const statusDotSize: Record<AvatarSize, string> = {
  xs: 'h-1.5 w-1.5',
  sm: 'h-2 w-2',
  md: 'h-2.5 w-2.5',
  lg: 'h-3 w-3',
  xl: 'h-3.5 w-3.5',
  '2xl': 'h-4 w-4',
  '3xl': 'h-5 w-5',
};

const statusColors = {
  online: 'bg-green-400',
  away: 'bg-yellow-400',
  offline: 'bg-slate-400',
};

export function Avatar({ src, name, size = 'md', className, status }: AvatarProps) {
  const initials = getInitials(name ?? undefined);
  const bgColor = name ? getAvatarColor(name) : 'bg-slate-500';

  const [imgError, setImgError] = React.useState(false);
  React.useEffect(() => {
    setImgError(false);
  }, [src]);
  const showImage = !!src && !imgError;

  return (
    <span className={cn('relative inline-flex flex-shrink-0', className)}>
      <span
        className={cn(
          'flex items-center justify-center rounded-full overflow-hidden',
          sizeStyles[size],
          !showImage && bgColor,
        )}
      >
        {showImage ? (
          <img
            src={src}
            alt={name ? `${name} avatar` : 'User avatar'}
            className="h-full w-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <span className="font-semibold text-white leading-none select-none">{initials}</span>
        )}
      </span>

      {status && (
        <span
          className={cn(
            'absolute bottom-0 right-0 block rounded-full ring-2 ring-white',
            statusDotSize[size],
            statusColors[status],
          )}
          aria-label={`Status: ${status}`}
        />
      )}
    </span>
  );
}
